import cn from 'utils/cn'
import { memo, ReactElement, ReactNode, useMemo } from 'react'
import { motion } from 'framer-motion'
import { IconType } from 'core/components/icons/types'
import Icon from 'core/components/icons/Icon'

interface TagProps {
  /**
   * Content to display inside the tag.
   */
  children: ReactNode
  /**
   * Icon or element to display before the tag content.
   */
  prefix?: `${IconType}` | ReactElement
  /**
   * Icon or element to display after the tag content.
   */
  suffix?: `${IconType}` | ReactElement
  /**
   * Visual style variant for the tag.
   */
  variant?: 'default' | 'primary' | 'danger' | 'outline'
  /**
   * Additional class names for the tag container.
   */
  className?: string
  /**
   * Value associated with the tag (used for identification).
   */
  value: string | number
  /**
   * Whether the tag is disabled.
   */
  disabled?: boolean
  /**
   * Callback when the tag is clicked, receives the tag's value.
   */
  onClick?: (value: string | number) => void
}

const variantStyles: Record<NonNullable<TagProps['variant']>, string> = {
  default: 'border border-transparent bg-dark-8 text-dark',
  primary: 'border border-transparent bg-primary-1 text-primary',
  danger: 'border border-transparent bg-danger-1 text-danger',
  outline: 'border border-dark-3 text-dark-3'
}
const motionVariants = {
  show: { scale: 1, opacity: 1 },
  hide: { scale: 0.7, opacity: 0 }
}

/**
 * Tag component for displaying labeled content with optional icons and variants.
 * Supports prefix/suffix icons, custom styles, and disabled state.
 */
const Tag = memo(
  ({
    children,
    variant = 'default',
    className,
    value,
    disabled,
    onClick,
    ...props
  }: TagProps) => {
    const prefix = useMemo(() => {
      if (props.prefix && (props.prefix as string) in IconType) {
        return (
          <Icon name={props.prefix as IconType} size={12} className="mr-2" />
        )
      }

      return props?.prefix ? (
        <span className="mr-2">{props?.prefix}</span>
      ) : null
    }, [props.prefix])

    const suffix = useMemo(() => {
      if (props.suffix && (props.suffix as string) in IconType) {
        return (
          <Icon name={props.suffix as IconType} size={12} className="ml-2" />
        )
      }

      return props?.suffix ? (
        <span className="ml-2">{props?.suffix}</span>
      ) : null
    }, [props.suffix])

    return (
      <motion.button
        type="button"
        value={value}
        variants={motionVariants}
        initial="hide"
        animate="show"
        exit="hide"
        disabled={disabled}
        className={cn(
          'inline-flex items-center rounded-full !leading-5 text-sm px-4 py-1 font-medium disabled:pointer-events-none',
          variantStyles[variant],
          className
        )}
        onClick={onClick ? () => onClick(value) : undefined}
      >
        {prefix}
        <span className="inline-block grow overflow-x-hidden text-ellipsis">
          {children}
        </span>
        {suffix}
      </motion.button>
    )
  }
)

export default Tag
